import Vue from 'vue';
import { generateClient } from 'aws-amplify/api';
import * as qlqueries from '../graphql/queries';
import * as customQueries from '../graphql/customQueries';
import * as customMutations from '../graphql/customMutations';

const client = generateClient();

const parseJsonSafe = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('EventAnalytics JSON parse error', error);
    return null;
  }
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeSeverity = (value) => {
  const key = (value || '').toString().trim().toLowerCase();
  if (key === 'error' || key === 'critical' || key === 'failed' || key === 'failure') return 'error';
  if (key === 'warning' || key === 'warn') return 'warning';
  if (key === 'info' || key === 'information' || key === 'notice') return 'info';
  return null;
};

// Three-tier model (see severity-threshold-strategy): info < warning < error.
// Legacy two-tier reports only ever produced warning/error, so they keep
// working unchanged.
const SEVERITY_RANK = { info: 1, warning: 2, error: 3 };

// Highest severity from a list, ignoring nullish entries; null when none.
const highestSeverity = (severities) => severities
  .filter(Boolean)
  .reduce((acc, sev) => ((SEVERITY_RANK[sev] || 0) > (SEVERITY_RANK[acc] || 0) ? sev : acc), null);

// Short label used in titles/tooltips. 'error' keeps reading as "critical" for
// continuity with the existing UI wording.
const severityLabel = (severity) => {
  if (severity === 'error') return 'critical';
  if (severity === 'info') return 'info';
  return 'warning';
};
const severityTitle = (severity) => {
  if (severity === 'error') return 'Critical anomaly';
  if (severity === 'info') return 'Info';
  return 'Warning';
};

const humanizeKey = (value) => (
  (value || '')
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
);

const normalizeMetricValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : trimmed;
  }
  if (typeof value === 'boolean') return value;
  return null;
};

const flattenMetricTree = (node, prefix = '') => {
  if (!node || typeof node !== 'object') return [];

  const entries = [];
  Object.keys(node).forEach((key) => {
    const value = node[key];
    const path = prefix ? `${prefix}.${key}` : key;
    const normalized = normalizeMetricValue(value);
    if (normalized !== null) {
      entries.push({
        key: path,
        label: humanizeKey(path),
        value: normalized,
      });
      return;
    }
    if (Array.isArray(value)) return;
    if (value && typeof value === 'object') {
      entries.push(...flattenMetricTree(value, path));
    }
  });

  return entries;
};

const toMs = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Incident timestamps in the report come without a timezone designator
// (e.g. "2026-05-22T16:08:30.705"). Event/report range timestamps carry a
// trailing "Z", so we interpret bare incident times as UTC to keep both on the
// same epoch when positioning bars on the timeline.
const toMsAssumeUtc = (value) => {
  if (typeof value === 'string' && value && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(value)) {
    return toMs(`${value}Z`);
  }
  return toMs(value);
};

const isGrafanaUrl = (value) => (
  typeof value === 'string'
  && value.includes('grafana-workspace')
  && (value.startsWith('http://') || value.startsWith('https://'))
);

const addGrafanaTimeRange = (url, fromMs, toMsValue) => {
  if (!isGrafanaUrl(url) || !fromMs || !toMsValue || toMsValue <= fromMs) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('from', String(fromMs));
    parsed.searchParams.set('to', String(toMsValue));
    return parsed.toString();
  } catch (error) {
    return url;
  }
};

const enrichGrafanaLinks = (value, fromMs, toMsValue, depth = 0) => {
  if (depth > 8 || value == null) return value;

  if (typeof value === 'string') {
    return addGrafanaTimeRange(value, fromMs, toMsValue);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => enrichGrafanaLinks(entry, fromMs, toMsValue, depth + 1));
  }

  if (typeof value === 'object') {
    const enriched = {};
    Object.keys(value).forEach((key) => {
      enriched[key] = enrichGrafanaLinks(value[key], fromMs, toMsValue, depth + 1);
    });
    return enriched;
  }

  return value;
};

// --- New report structure normalization -------------------------------------

const normalizeAnomaly = (item) => {
  if (!item || typeof item !== 'object') return null;
  const severity = normalizeSeverity(item.severity || item.level);
  const startMs = toMsAssumeUtc(item.startTime || item.start);
  const endMsRaw = toMsAssumeUtc(item.endTime || item.end);
  const endMs = endMsRaw || startMs;
  if (!severity || !startMs || !endMs || endMs < startMs) return null;

  const resourceType = (item.resourceType || '').toString().toUpperCase();
  const aiAnalysis = item.aiAnalysis && typeof item.aiAnalysis === 'object' ? item.aiAnalysis : null;
  const metricName = item.metricName || '';

  return {
    id: item.id || `${severity}-${startMs}-${metricName}`,
    severity,
    startMs,
    endMs,
    metricName,
    resourceId: item.resourceId || '',
    resourceName: item.resourceName || item.resourceId || '',
    resourceType,
    sourceType: item.sourceType || '',
    groupId: item.groupId || '',
    groupName: item.groupName || '',
    groupClass: item.groupClass || '',
    aiAnalysis,
    hasAi: !!aiAnalysis,
    title: metricName ? `${metricName} ${severityLabel(severity)}` : severityTitle(severity),
    description: (aiAnalysis && aiAnalysis.whatHappened) || '',
    rootCause: (aiAnalysis && aiAnalysis.probableCause) || '',
    raw: item,
  };
};

// Newer reports ship incidents already grouped: each `incidents[]` entry is an
// incident with nested `anomalies[]`, and each anomaly carries `occurrences[]`
// (the real time windows). The resource info (metricName/resourceType/...) and
// the per-occurrence stats live on those nested levels, while `aiAnalysis` sits
// on the incident. We expand each occurrence into one flat anomaly so the
// timeline/details components — which expect flat anomalies — keep working.
const normalizeNestedAnomaly = (incident, anomaly, occurrence, index) => {
  const occ = occurrence || {};
  const severity = normalizeSeverity(occ.severity || anomaly.severity || incident.severity);
  const startMs = toMsAssumeUtc(occ.startTime || occ.start || incident.startTime);
  const endMsRaw = toMsAssumeUtc(occ.endTime || occ.end || incident.endTime);
  const endMs = endMsRaw || startMs;
  if (!severity || !startMs || !endMs || endMs < startMs) return null;

  const resourceType = (anomaly.resourceType || '').toString().toUpperCase();
  const metricName = occ.metricName || anomaly.metricName || '';
  const aiAnalysis = incident.aiAnalysis && typeof incident.aiAnalysis === 'object' ? incident.aiAnalysis : null;
  const stats = (occ.stats && typeof occ.stats === 'object')
    ? occ.stats
    : ((anomaly.stats && typeof anomaly.stats === 'object') ? anomaly.stats : null);

  return {
    id: `${incident.id || severity}-${metricName || 'metric'}-${index}`,
    // Real incident id so the "Analyze group" action can target the backend
    // incident rather than this synthetic per-occurrence id.
    incidentId: incident.id || '',
    severity,
    startMs,
    endMs,
    metricName,
    resourceId: anomaly.resourceId || '',
    resourceName: anomaly.resourceName || anomaly.resourceId || '',
    resourceType,
    sourceType: anomaly.sourceType || '',
    // Signal group (G1–G11) — anomaly's own, falling back to the incident's.
    groupId: anomaly.groupId || incident.groupId || '',
    groupName: anomaly.groupName || incident.groupName || '',
    groupClass: anomaly.groupClass || incident.groupClass || '',
    aiAnalysis,
    hasAi: !!aiAnalysis,
    title: metricName ? `${metricName} ${severityLabel(severity)}` : severityTitle(severity),
    description: (aiAnalysis && aiAnalysis.whatHappened) || '',
    rootCause: (aiAnalysis && aiAnalysis.probableCause) || '',
    raw: { ...anomaly, stats, config: anomaly.config || incident.config },
  };
};

// Build a timeline group straight from a pre-grouped incident (new structure).
const normalizeIncidentGroup = (incident) => {
  if (!incident || typeof incident !== 'object') return null;
  const anomalies = [];
  (Array.isArray(incident.anomalies) ? incident.anomalies : []).forEach((anomaly) => {
    if (!anomaly || typeof anomaly !== 'object') return;
    const occurrences = Array.isArray(anomaly.occurrences) && anomaly.occurrences.length
      ? anomaly.occurrences
      : [null];
    occurrences.forEach((occ) => {
      const normalized = normalizeNestedAnomaly(incident, anomaly, occ, anomalies.length);
      if (normalized) anomalies.push(normalized);
    });
  });
  if (!anomalies.length) return null;

  const startMs = toMsAssumeUtc(incident.startTime) || Math.min(...anomalies.map((a) => a.startMs));
  const endMs = toMsAssumeUtc(incident.endTime) || Math.max(...anomalies.map((a) => a.endMs));
  // Group severity is the highest of the incident-level severity and any of its
  // anomalies (error > warning > info), so a pure-INFO incident stays INFO.
  const severity = highestSeverity([normalizeSeverity(incident.severity), ...anomalies.map((a) => a.severity)]) || 'info';
  const resourceTypes = [];
  anomalies.forEach((a) => {
    if (a.resourceType && !resourceTypes.includes(a.resourceType)) resourceTypes.push(a.resourceType);
  });
  const critical = anomalies.filter((a) => a.severity === 'error').length;
  const warning = anomalies.filter((a) => a.severity === 'warning').length;
  const info = anomalies.filter((a) => a.severity === 'info').length;

  return {
    id: incident.id || `grp-${startMs}`,
    severity,
    startMs,
    endMs,
    resourceTypes,
    // Incident's root signal group (G1–G11).
    groupId: incident.groupId || '',
    groupName: incident.groupName || '',
    groupClass: incident.groupClass || '',
    anomalies,
    criticalCount: critical,
    warningCount: warning,
    infoCount: info,
    title: `${anomalies.length} ${anomalies.length === 1 ? 'anomaly' : 'anomalies'}`,
  };
};

// Cluster individual anomalies into incidents shown on the main timeline.
const GROUP_GAP_MS = 30000;

const groupIncidents = (anomalies) => {
  const sorted = [...anomalies].sort((a, b) => a.startMs - b.startMs);
  const groups = [];

  sorted.forEach((anomaly) => {
    const last = groups[groups.length - 1];
    if (!last || anomaly.startMs - last.endMs > GROUP_GAP_MS) {
      groups.push({
        id: `grp-${anomaly.startMs}-${anomaly.id}`,
        severity: anomaly.severity,
        startMs: anomaly.startMs,
        endMs: anomaly.endMs,
        resourceTypes: anomaly.resourceType ? [anomaly.resourceType] : [],
        anomalies: [anomaly],
      });
      return;
    }
    last.endMs = Math.max(last.endMs, anomaly.endMs);
    if (SEVERITY_RANK[anomaly.severity] > SEVERITY_RANK[last.severity]) {
      last.severity = anomaly.severity;
    }
    if (anomaly.resourceType && !last.resourceTypes.includes(anomaly.resourceType)) {
      last.resourceTypes.push(anomaly.resourceType);
    }
    last.anomalies.push(anomaly);
  });

  return groups.map((group) => {
    const critical = group.anomalies.filter((a) => a.severity === 'error').length;
    const warning = group.anomalies.filter((a) => a.severity === 'warning').length;
    const info = group.anomalies.filter((a) => a.severity === 'info').length;
    return {
      ...group,
      criticalCount: critical,
      warningCount: warning,
      infoCount: info,
      title: `${group.anomalies.length} ${group.anomalies.length === 1 ? 'anomaly' : 'anomalies'}`,
    };
  });
};

const normalizeSummary = (reportData) => {
  const exec = (reportData && reportData.execSummary) || {};
  const totals = exec.totals && typeof exec.totals === 'object'
    ? exec.totals
    : ((reportData && reportData.stats && reportData.stats.totals) || {});
  // New reports report `errorCount`; legacy reports used `criticalCount`. Keep
  // `criticalCount` populated (existing UI reads it) and also expose
  // `errorCount`/`infoCount` for the three-tier display.
  const errorCount = toNumber(exec.errorCount != null ? exec.errorCount : exec.criticalCount);
  return {
    status: exec.status || null,
    incidentCount: toNumber(exec.incidentCount),
    criticalCount: errorCount,
    errorCount,
    warningCount: toNumber(exec.warningCount),
    infoCount: toNumber(exec.infoCount),
    durationSeconds: toNumber(exec.durationSeconds != null ? exec.durationSeconds : (reportData && reportData.durationSeconds)),
    highlights: Array.isArray(exec.highlights)
      ? exec.highlights.filter((h) => typeof h === 'string' && h.trim() !== '')
      : [],
    totals,
  };
};

const normalizeAiEventSummary = (reportData) => {
  let value = reportData && reportData.aiEventSummary;
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  if (!value || typeof value !== 'object') return null;

  const cleanList = (list) => (Array.isArray(list)
    ? list.filter((item) => typeof item === 'string' && item.trim() !== '').map((item) => item.trim())
    : []);

  return {
    generatedAt: value.generatedAt || null,
    keyFindings: cleanList(value.keyFindings),
    model: value.model || '',
    recommendations: cleanList(value.recommendations),
    summary: typeof value.summary === 'string' ? value.summary.trim() : '',
  };
};

const normalizeMetrics = (reportData) => {
  const totalsObj = (reportData && reportData.stats && reportData.stats.totals)
    || (reportData && reportData.execSummary && reportData.execSummary.totals)
    || {};
  const metricTotals = flattenMetricTree(totalsObj);

  const perResource = reportData && reportData.stats && reportData.stats.perResource;
  const resourceMetrics = [];
  if (Array.isArray(perResource)) {
    perResource.forEach((resource) => {
      resourceMetrics.push({
        resourceId: (resource && resource.resourceId) || '',
        resourceName: (resource && (resource.resourceName || resource.resourceId)) || 'Unnamed',
        resourceType: ((resource && resource.resourceType) || '').toString().toUpperCase(),
        sourceType: (resource && resource.sourceType) || '',
        metrics: flattenMetricTree((resource && resource.metrics) || {}),
      });
    });
  }

  return {
    metricTotals,
    resourceMetrics,
    thresholds: (reportData && reportData.thresholds) || {},
  };
};

const normalizeEventAnalytics = ({ event, reportData }) => {
  const eventFromMs = toMs(event && (event.lineupTime || event.startTime || event.serviceStartTime));
  const eventToMs = toMs(event && (event.endTime || event.serviceEndTime));

  const incidentsRaw = Array.isArray(reportData && reportData.incidents)
    ? reportData.incidents
    : [];

  // New reports nest anomalies under each incident; legacy reports list flat
  // anomalies that we cluster by time gap.
  const isNestedShape = incidentsRaw.some((item) => item && Array.isArray(item.anomalies));
  let incidentGroups = isNestedShape
    ? incidentsRaw.map(normalizeIncidentGroup).filter(Boolean)
    : groupIncidents(incidentsRaw.map(normalizeAnomaly).filter(Boolean));

  if (eventFromMs && eventToMs && eventToMs > eventFromMs) {
    incidentGroups = enrichGrafanaLinks(incidentGroups, eventFromMs, eventToMs);
  }
  const anomalies = incidentGroups.reduce((acc, group) => acc.concat(group.anomalies || []), []);
  const summary = normalizeSummary(reportData);
  const aiEventSummary = normalizeAiEventSummary(reportData);
  const metrics = normalizeMetrics(reportData);

  return {
    summary,
    aiEventSummary,
    incidentGroups,
    anomalies,
    dataRange: {
      from: toMs(reportData && reportData.startTime),
      to: toMs(reportData && reportData.endTime),
    },
    ...metrics,
  };
};

const emptyNormalized = () => ({
  summary: {
    status: null,
    incidentCount: 0,
    criticalCount: 0,
    errorCount: 0,
    warningCount: 0,
    infoCount: 0,
    durationSeconds: 0,
    highlights: [],
    totals: {},
  },
  aiEventSummary: null,
  incidentGroups: [],
  anomalies: [],
  dataRange: { from: null, to: null },
  metricTotals: [],
  resourceMetrics: [],
  thresholds: {},
});

const REPORT_READY_STATUSES = ['ready', 'complete', 'completed', 'generated', 'done'];

const isReadyStatus = (status) => {
  if (!status) return false;
  return REPORT_READY_STATUSES.includes(`${status}`.trim().toLowerCase());
};

const state = {
  loadingRaw: false,
  loadingReport: false,
  generatingReport: false,
  analyzingIncidents: false,
  loadingEvents: false,
  events: [],
  rawDataByEventId: {},
  reportByEventId: {},
  reportStatusByEventId: {},
  normalizedByEventId: {},
  // Live report/AI-analysis progress fed by the WebSocket channel, keyed by
  // eventId. Bumped seq + lastSignal let the UI fire a toast on each event.
  reportProgressByEventId: {},
  lastReportSignal: null,
  lastReportSignalSeq: 0,
};

const getters = {
  getEventRawData: (state) => (eventId) => state.rawDataByEventId[eventId] || null,
  getEventReport: (state) => (eventId) => state.reportByEventId[eventId] || null,
  getEventReportStatus: (state) => (eventId) => state.reportStatusByEventId[eventId] || null,
  hasEventReport: (state) => (eventId) => {
    const report = state.reportByEventId[eventId];
    if (!report) return false;
    if (report.status) return isReadyStatus(report.status);
    return !!(report.data || report.reportId || report.id);
  },
  getEventNormalized: (state) => (eventId) => state.normalizedByEventId[eventId] || null,
  getReportProgress: (state) => (eventId) => state.reportProgressByEventId[eventId] || null,
};

const mutations = {
  setLoadingRaw: (state, payload) => {
    state.loadingRaw = payload;
  },
  setLoadingReport: (state, payload) => {
    state.loadingReport = payload;
  },
  setGeneratingReport: (state, payload) => {
    state.generatingReport = payload;
  },
  setAnalyzingIncidents: (state, payload) => {
    state.analyzingIncidents = payload;
  },
  setLoadingEvents: (state, payload) => {
    state.loadingEvents = payload;
  },
  setEvents: (state, payload) => {
    state.events = payload || [];
  },
  // Merge a single event into the list (add or replace by id). Used by
  // deep-links where the target event may fall outside the current date range.
  upsertEvent: (state, event) => {
    if (!event || !event.id) return;
    const index = state.events.findIndex((item) => item && item.id === event.id);
    if (index === -1) state.events = [...state.events, event];
    else Vue.set(state.events, index, event);
  },
  setRawData: (state, { eventId, payload }) => {
    // Vue.set for targeted reactivity — avoids O(n^2) reallocation of the whole
    // map while many reports stream in.
    Vue.set(state.rawDataByEventId, eventId, payload);
  },
  setReportData: (state, { eventId, payload }) => {
    Vue.set(state.reportByEventId, eventId, payload);
  },
  setReportStatus: (state, { eventId, status }) => {
    Vue.set(state.reportStatusByEventId, eventId, status || null);
  },
  setNormalizedData: (state, { eventId, payload }) => {
    Vue.set(state.normalizedByEventId, eventId, payload);
  },
  setReportProgress: (state, { eventId, progress }) => {
    Vue.set(state.reportProgressByEventId, eventId, progress);
    state.lastReportSignal = progress;
    state.lastReportSignalSeq += 1;
  },
};

// AppSync returns partial data alongside `errors` when a non-nullable nested
// field comes back null (e.g. a deleted `account`, or `outputs.items: [null]`).
// Amplify surfaces that as a thrown error that still carries `.data`. Recover
// the rows that did resolve and drop the null nested entries.
const cleanEvent = (event) => {
  if (!event) return event;
  if (event.outputs?.items) {
    event.outputs.items = event.outputs.items.filter(Boolean);
  }
  if (event.inputs?.items) {
    event.inputs.items = event.inputs.items.filter(Boolean);
  }
  return event;
};

const actions = {
  async fetchEventAnalyticsEvents({ commit }, { start, end } = {}) {
    commit('setLoadingEvents', true);
    const toItems = (result) =>
      (result?.data?.eventsByStartTime?.items || []).filter(Boolean).map(cleanEvent);
    try {
      const result = await client.graphql({
        query: qlqueries.eventsByStartTimeExtended,
        variables: {
          type: 'OU',
          sortDirection: 'ASC',
          limit: 1500,
          startTime: {
            between: [start, end],
          },
        },
      });
      const items = toItems(result);
      commit('setEvents', items);
      return items;
    } catch (error) {
      // Partial response: keep the events that resolved.
      if (error?.data?.eventsByStartTime) {
        console.warn('fetchEventAnalyticsEvents partial data', error.errors);
        const items = toItems(error);
        commit('setEvents', items);
        return items;
      }
      console.error('fetchEventAnalyticsEvents error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setLoadingEvents', false);
    }
  },

  // Fetch a single event by id, regardless of any date range, and merge it into
  // the events list. Used for deep-links to a report whose event may sit outside
  // the persisted filter range (where the range query wouldn't return it).
  async fetchEventAnalyticsEventById({ commit, state }, { eventId } = {}) {
    if (!eventId) return null;
    const existing = (state.events || []).find((item) => item && item.id === eventId);
    if (existing) return existing;
    commit('setLoadingEvents', true);
    try {
      const result = await client.graphql({
        query: qlqueries.getEvent,
        variables: { id: eventId },
      });
      const event = cleanEvent(result?.data?.getEvent || null);
      if (event) commit('upsertEvent', event);
      return event;
    } catch (error) {
      // Partial response: the event resolved even if a nested field was null.
      const event = cleanEvent(error?.data?.getEvent || null);
      if (event) {
        console.warn('fetchEventAnalyticsEventById partial data', error.errors);
        commit('upsertEvent', event);
        return event;
      }
      console.error('fetchEventAnalyticsEventById error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setLoadingEvents', false);
    }
  },

  // Lightweight: fetch report meta (existence/status/id) for many events in a
  // single request. Full report `data` is loaded later per report.
  async fetchEventAnalyticsReportsBatch({ commit, state }, { eventIds } = {}) {
    const ids = (eventIds || []).filter(Boolean);
    if (ids.length === 0) return { success: true, items: [] };
    commit('setLoadingReport', true);
    try {
      const result = await client.graphql({
        query: customQueries.eventAnalyticsReportsBatch,
        variables: { eventIds: ids },
      });
      const response = result?.data?.eventAnalyticsReportsBatch;
      const items = response?.items || [];
      items.forEach((item) => {
        if (!item || !item.eventId) return;
        // Keep any full data already loaded for this event.
        const existing = state.reportByEventId[item.eventId];
        commit('setReportData', {
          eventId: item.eventId,
          payload: {
            id: item.reportId,
            reportId: item.reportId,
            eventId: item.eventId,
            status: item.status,
            generatedAt: item.generatedAt,
            data: (existing && existing.data) || null,
          },
        });
        commit('setReportStatus', { eventId: item.eventId, status: item.status });
      });
      return { success: !!response?.success, message: response?.message, items };
    } catch (error) {
      console.error('fetchEventAnalyticsReportsBatch error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setLoadingReport', false);
    }
  },

  async fetchEventAnalyticsRawData({ commit }, { eventId }) {
    commit('setLoadingRaw', true);
    try {
      const result = await client.graphql({
        query: customQueries.getEventReportData,
        variables: { eventId },
      });

      const response = result?.data?.getEventReportData;
      const rawData = parseJsonSafe(response?.dataJson) || response?.dataJson || null;
      commit('setRawData', { eventId, payload: rawData });
      return { success: !!response?.success, message: response?.message, data: rawData };
    } catch (error) {
      console.error('fetchEventAnalyticsRawData error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setLoadingRaw', false);
    }
  },

  // Fetch the FULL report (with `data`) for a single event. Resolves the
  // reportId from the meta loaded by the batch query, or via a meta lookup.
  async fetchEventAnalyticsReport({ commit, state, dispatch }, { eventId, reportId } = {}) {
    if (!eventId) return null;
    commit('setLoadingReport', true);
    try {
      let resolvedReportId = reportId
        || (state.reportByEventId[eventId] && state.reportByEventId[eventId].reportId);

      if (!resolvedReportId) {
        // Discover the reportId with a lightweight meta lookup.
        const metaResult = await dispatch('fetchEventAnalyticsReportsBatch', { eventIds: [eventId] });
        const metaItem = (metaResult && metaResult.items && metaResult.items[0]) || null;
        resolvedReportId = metaItem && metaItem.reportId;
      }

      if (!resolvedReportId) {
        commit('setReportData', { eventId, payload: null });
        commit('setReportStatus', { eventId, status: null });
        return null;
      }

      const result = await client.graphql({
        query: customQueries.getEventAnalyticsReport,
        variables: { id: resolvedReportId },
      });
      const report = result?.data?.getEventAnalyticsReport || null;
      const existing = state.reportByEventId[eventId];
      const normalized = {
        id: resolvedReportId,
        reportId: resolvedReportId,
        eventId,
        status: (existing && existing.status) || 'READY',
        generatedAt: (report && (report.updatedAt || report.createdAt)) || (existing && existing.generatedAt) || null,
        data: report ? (parseJsonSafe(report.data) || report.data || null) : null,
      };
      commit('setReportData', { eventId, payload: normalized });
      commit('setReportStatus', { eventId, status: normalized.status });
      return normalized;
    } catch (error) {
      console.error('fetchEventAnalyticsReport error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setLoadingReport', false);
    }
  },

  async generateEventAnalyticsReport({ commit }, { eventId, config }) {
    commit('setGeneratingReport', true);
    try {
      const configPayload = typeof config === 'string' ? config : JSON.stringify(config || {});
      const result = await client.graphql({
        query: customMutations.generateEventReport,
        variables: {
          eventId,
          config: configPayload,
        },
      });
      const response = result?.data?.generateEventReport;
      return {
        success: !!response?.success,
        message: response?.message,
        data: parseJsonSafe(response?.dataJson) || response?.dataJson,
      };
    } catch (error) {
      console.error('generateEventAnalyticsReport error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setGeneratingReport', false);
    }
  },

  // Run AI analysis for selected incidents ("all" or an array of incident ids).
  async analyzeEventAnalyticsIncidents({ commit }, { reportId, incidentIds, config }) {
    commit('setAnalyzingIncidents', true);
    try {
      const incidentIdsPayload = typeof incidentIds === 'string' && incidentIds.trim().startsWith('[')
        ? incidentIds
        : JSON.stringify(incidentIds);
      const configPayload = typeof config === 'string' ? config : JSON.stringify(config || {});
      const result = await client.graphql({
        query: customMutations.analyzeEventReportIncidents,
        variables: {
          reportId,
          incidentIds: incidentIdsPayload,
          config: configPayload,
        },
      });
      const response = result?.data?.analyzeEventReportIncidents;
      return {
        success: !!response?.success,
        message: response?.message,
        data: parseJsonSafe(response?.dataJson) || response?.dataJson,
      };
    } catch (error) {
      console.error('analyzeEventAnalyticsIncidents error', error);
      return { error: true, message: error.errors || error.message };
    } finally {
      commit('setAnalyzingIncidents', false);
    }
  },

  // Apply a ReportAnalysisNotification pushed over the WebSocket channel
  // (forwarded by store/LiveNotifications). Records progress for the UI and,
  // when a stage produces new persisted data, refetches the full report so the
  // open report view updates without a manual reload.
  async applyReportNotification({ commit, dispatch, state }, notification) {
    const { eventType, eventId, reportId } = notification || {};
    if (!eventId || !eventType) return;

    commit('setReportProgress', {
      eventId,
      progress: {
        eventType,
        reportId: reportId || null,
        status: notification.payload?.status || null,
        error: notification.payload?.error || null,
        payload: notification.payload || null,
        timestamp: notification.timestamp || null,
        receivedAt: Date.now(),
      },
    });

    if (eventType === 'report_generation_completed') {
      // Report now exists server-side — mark ready and pull the full document.
      commit('setReportStatus', { eventId, status: 'completed' });
    }

    // Stages that persist new report data: refetch so the view reflects it.
    const REFETCH_ON = new Set([
      'report_generation_completed',
      'ai_analysis_event_summary_completed',
      'ai_analysis_completed',
    ]);
    if (REFETCH_ON.has(eventType)) {
      const result = await dispatch('fetchEventAnalyticsReport', {
        eventId,
        reportId,
      });
      if (result && !result.error) {
        const event = (state.events || [])
          .find((item) => item && item.id === eventId);
        if (event) dispatch('normalizeEventAnalytics', { event });
      }
    }
  },

  normalizeEventAnalytics({ commit, state }, { event }) {
    const eventId = event?.id;
    if (!eventId) return null;
    const reportContainer = state.reportByEventId[eventId];
    const reportData = reportContainer ? (reportContainer.data || reportContainer) : null;
    const payload = reportData
      ? normalizeEventAnalytics({ event, reportData })
      : emptyNormalized();
    commit('setNormalizedData', { eventId, payload });
    return payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
